import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { tDynamic } from '@/shared/lib/i18n';
import { useAuthStore, useCan } from '@/entities/session';
import { Perm } from '@/shared/lib/permission-codes';
import { TodaySummary } from '@/widgets/today-summary';
import { MemberCards } from '@/widgets/member-cards';
import { FamilyQuestCard } from '@/widgets/family-quest';
import { useHouseholdContext } from '@/shared/lib/household';

/**
 * Family dashboard (mockup 02 screen 1, parent view): a warm greeting, "today at a glance" (real
 * approvals count), the cooperative family quest card (ADR-0054 — the one shared cooperative
 * feature), and the "our kids" member cards with live balances. Approvals summary only renders when
 * the user can approve (authz-aware UI). An empty roster gets an add-child CTA from the member-cards
 * widget itself — there's no separate setup flow.
 */
/**
 * The greeting bucket for `hour` (0–23). Computed from the household-local hour (not the browser's,
 * and re-read each render so it never stalls past midnight, ADR-0023).
 */
function greetingKey(
  hour: number,
): 'dashboard.greetingMorning' | 'dashboard.greetingAfternoon' | 'dashboard.greetingEvening' {
  if (hour < 12) return 'dashboard.greetingMorning';
  if (hour < 18) return 'dashboard.greetingAfternoon';
  return 'dashboard.greetingEvening';
}

export function FamilyDashboardPage(): React.ReactElement {
  const { t } = useTranslation('family');
  const user = useAuthStore((s) => s.user);
  const { can } = useCan();
  const { timeZone } = useHouseholdContext();

  // The household-local hour (ADR-0023) — computed per render, so the greeting follows the household's
  // clock and rolls over at its midnight, not the device's.
  const localHour = Number(
    new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', hour: '2-digit' }).format(
      new Date(),
    ),
  );
  const greeting = greetingKey(localHour);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <header className="px-1">
        <p className="text-sm font-medium text-text-muted">{t(greeting)}</p>
        {/* The greeting names the guardian as the page heading (mockup 05-home). The name is the
            guardian's own display name — never concatenated into a translated grammar (ADR-0025). */}
        {user && (
          <h1 className="font-display text-2xl font-bold tracking-tight text-text">
            {user.displayName}
          </h1>
        )}
      </header>

      {/* "Today at a glance" surfaces the "to approve" count as a KPI tile that links to the Inbox;
          the global awaiting-decision signal also rides the bottom-nav Inbox badge (AppShell chrome).
          No separate decisions card here — that would duplicate both (mockup 05-home). */}
      {can(Perm.OccurrenceApprove) && <TodaySummary />}
      <FamilyQuestCard />

      {/* "Our kids" section: its own header row carries the section heading AND a link to the full
          all-children overview, so the member list below has a heading directly above it. */}
      <section className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-xs font-bold uppercase tracking-wide text-text-muted">
            {t('dashboard.kidsTitle')}
          </h2>
          <Link to="/family" className="text-xs font-semibold text-primary hover:underline">
            {tDynamic(t, 'dashboard.kidsViewAll')}
          </Link>
        </div>
        {/* The header row above names the section, so the widget drops its own heading to avoid
            announcing the same title twice (one accessible heading per section). */}
        <MemberCards showHeading={false} />
      </section>
    </div>
  );
}
